<template>
  <div class="status-selector">
    <v-progress-circular v-if="updateProcessing" indeterminate :size="24" />
    <v-menu
      v-else
      offset-y
      :close-on-content-click="true"
      :disabled="selectorDisabled || optionsLoading"
    >
      <template v-slot:activator="{ on, attrs }">
        <div
          class="status-selector__activator"
          v-bind="attrs"
          v-on="on"
          @click.stop
        >
          <v-chip
            class="ma-0 status-selector__chip"
            :color="statusColor"
            :text-color="chipTextColor"
            label
          >
            <span class="status-selector__chip-text">{{ currentStatus }}</span>
            <v-icon
              v-if="!selectorDisabled && !optionsLoading"
              right
              small
              class="status-selector__chip-caret"
              :style="{ color: chipTextColor }"
              >mdi-chevron-down</v-icon
            >
          </v-chip>
        </div>
      </template>

      <!-- Grouped status menu with subheaders (mirrors the assignment form UI) -->
      <v-list dense class="status-selector__list">
        <template v-for="group in groupedOptions">
          <v-subheader
            :key="`${group.label}-header`"
            class="status-selector__subheader"
          >
            {{ group.label }}
          </v-subheader>
          <v-list-item
            v-for="option in group.items"
            :key="option.value"
            :disabled="option.disabled"
            class="status-selector__item"
            @click="updateStatus(option)"
          >
            <v-list-item-content>
              <v-list-item-title class="status-selector__item-title">
                <v-icon left :size="14" :color="option.color">mdi-circle</v-icon>
                {{ option.value }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-list>
    </v-menu>
  </div>
</template>

<script>
// The status cell for the V2 dashboard (ADTableV2), split from ADStatusSelector
// the same way ADTableV2 is split from ADTable: V1 keeps its original v-select
// unchanged, and only V2 gets the chip + grouped-menu redesign. The props, the
// emitted events and every side effect below onChange are deliberately kept
// identical between the two, so a fix to the status-change flow has to be
// applied in both files.

// vuex
import { mapActions } from "vuex";
// internal
import {
  processAssignment,
  getAssignments,
  getTasks,
  processTask,
  getStaffHoursByAssignmentId,
  updateStaffHoursStatus,
} from "@/utils/newDbUtils";

// Assignment statuses that send an assignment back into the hours flow.
// When the assignment returns to one of these, any already-approved hours
// submission must be reopened (reset to PENDING) so staff/reviewers regain
// the change/remove controls.
const HOURS_FLOW_STATUSES = ["Pending Hours", "Reviewing Hours"];

// Display grouping for the assignment status dropdown. Mirrors the grouping and
// dot colors used by the assignment form (ADFormComponents/MainTab.vue) so the
// dashboard row and the form stay visually consistent. This is presentation
// only — the selectable statuses and their enabled/disabled state still come
// from the `options` / `roleBasedOptions` logic below.
const STATUS_GROUPS = [
  { label: "INITIATION", statuses: ["Requested", "Confirmed"] },
  {
    label: "IN PROGRESS",
    statuses: [
      "Assigned",
      "Rejected",
      "In Progress",
      "Design in Progress",
      "Primary Complete",
      "Secondary Complete",
      "QC Complete",
    ],
  },
  {
    label: "BLOCKED",
    statuses: [
      "Pending Hours",
      "Reviewing Hours",
      "Pending Review",
      "On Hold",
      "Quantity Needed",
    ],
  },
  {
    label: "PRE-DELIVERY",
    statuses: ["Ready for Delivery", "Client Reviewed", "Reported to Client"],
  },
  {
    label: "FINISHED",
    statuses: [
      "Delivered",
      "Canceled",
      "Cancelled",
      "Canceled (Billed)",
      "Canceled (Not Billed)",
      "Resolved",
      "Archived",
    ],
  },
];

const STATUS_DOT_COLORS = {
  Requested: "#7BC4C480",
  Confirmed: "#7BC4C4",
  Assigned: "#8CA9C3",
  "Primary Complete": "#9FB8F8",
  "Secondary Complete": "#567DDF",
  "QC Complete": "#3F599B",
  "Pending Hours": "#F1DE99",
  "Reviewing Hours": "#EFCD56",
  "On Hold": "#EBCF6D",
  "Ready for Delivery": "#C29DF4",
  Delivered: "#70D9A4",
  Canceled: "#FFA1A6",
  Cancelled: "#FFA1A6",
};

export default {
  name: "ADStatusSelectorV2",
  props: {
    item: {
      type: Object,
      required: true,
    },
    value: {
      type: String,
      required: true,
    },
    options: {
      type: Array,
      required: true,
    },
    optionsLoading: {
      type: Boolean,
      required: true,
    },
    roleMode: {
      type: String,
      required: true,
    },
    writerStatuses: {
      type: Array,
      required: true,
    },
    editorStatuses: {
      type: Array,
      required: true,
    },
    reviewerStatuses: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      currentStatus: null,
      updateProcessing: false,
      itemUpdate: 0,
    };
  },
  computed: {
    statusColor() {
      return this.dotColor(this.currentStatus);
    },
    chipTextColor() {
      return this.textColor(this.statusColor);
    },
    roleBasedOptions() {
      return this.options
        .map((el) => ({
          ...el,
          disabled: !this.allowedToSelectStatuses().includes(el.value),
        }))
        .sort((a, b) => Number(a.disabled) - Number(b.disabled));
    },
    // In role mode the whole selector is locked when the current status is not
    // one the active user is allowed to change (same rule ADStatusSelector's
    // v-select applies via its :disabled binding).
    selectorDisabled() {
      return this.roleMode
        ? !this.allowedToUpdateStatuses().includes(this.currentStatus)
        : false;
    },
    // Bucket the currently selectable options into the display groups above,
    // preserving each option's disabled flag. Any status not covered by a group
    // still shows (under "OTHER") so nothing is ever hidden from the menu.
    groupedOptions() {
      const source = this.roleMode ? this.roleBasedOptions : this.options;
      const byValue = {};
      source.forEach((el) => {
        byValue[el.value] = el;
      });
      const used = new Set();
      const groups = STATUS_GROUPS.map((group) => {
        const items = [];
        group.statuses.forEach((status) => {
          const option = byValue[status];
          if (option && !used.has(status)) {
            items.push({
              value: status,
              color: this.dotColor(status),
              disabled: !!option.disabled,
            });
            used.add(status);
          }
        });
        return { label: group.label, items };
      }).filter((group) => group.items.length);

      const others = source
        .filter((el) => !used.has(el.value))
        .map((el) => ({
          value: el.value,
          color: this.dotColor(el.value),
          disabled: !!el.disabled,
        }));
      if (others.length) groups.push({ label: "OTHER", items: others });

      return groups;
    },
  },
  watch: {
    value(newValue) {
      this.currentStatus = newValue;
    },
  },
  mounted() {
    this.currentStatus = this.value;
  },
  methods: {
    ...mapActions("flashMessage", ["handleFlash"]),
    dotColor(status) {
      return (
        STATUS_DOT_COLORS[status] ||
        this.options.find((el) => el.value === status)?.color ||
        "#E0E0E0"
      );
    },
    // Pick a readable chip text color for the given background, since the status
    // palette spans very light (Requested) to dark (QC Complete) swatches.
    textColor(hex) {
      if (!hex) return "#3c4043";
      let h = hex.replace("#", "").slice(0, 6);
      if (h.length === 3) {
        h = h
          .split("")
          .map((c) => c + c)
          .join("");
      }
      if (h.length < 6) return "#3c4043";
      const r = parseInt(h.slice(0, 2), 16);
      const g = parseInt(h.slice(2, 4), 16);
      const b = parseInt(h.slice(4, 6), 16);
      const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
      return luminance > 150 ? "#3c4043" : "#FFFFFF";
    },
    updateStatus(option) {
      if (option.disabled || option.value === this.currentStatus) return;
      this.onChange({ item: this.item, selected: option.value });
    },
    onChange({ item, selected }) {
      if (item.project.id === 115
        && item.assignmentDetails.requestType === "Adaptation"
        && selected === "Delivered"
        && (!item.assignmentDetails.wordCount || !item.pricing.workTypes[0].quantity)
      ) {
        this.itemUpdate += 1;
        this.$emit("show-warning-modal", true);
        return;
      }
      this.currentStatus = selected;
      this.updateProcessing = true;
      processAssignment({
        data: {
          assignmentStatus: selected,
        },
        id: item.id,
        patch: true,
      })
        .then((resp) => {
          if (resp.status > 204) {
            this.updateProcessing = false;
            this.handleFlash({ response: resp, show: true });
            console.error(resp);
          } else {
            this.$emit("refresh-table-data", item.index, selected);
            // handle task status after assignment status change
            if (selected.endsWith("Complete")) {
              getAssignments({ query: { search: item.cueTaskId } })
                .then((assgnResp) => {
                  const assgnsWithCueIdField = assgnResp.content.filter(
                    (el) => el.cueTaskId === item.cueTaskId
                  );
                  if (
                    assgnsWithCueIdField.every(
                      (el) => el.assignmentDetails.assignmentStatus === selected
                    )
                  ) {
                    getTasks({ query: { search: item.cueTaskId } })
                      .then((taskResp) => {
                        const task = taskResp.content.find(
                          (el) => el.cueTaskId === item.cueTaskId
                        );
                        if (task) {
                          processTask({
                            data: {
                              taskStatus: selected,
                            },
                            id: task.id,
                            patch: true,
                          }).then((resp) =>
                            this.handleFlash({ response: resp, show: true })
                          );
                        }
                      })
                      .catch((err) => {
                        console.error(err);
                        this.handleFlash({ response: err, show: true });
                      });
                  }
                  this.updateProcessing = false;
                })
                .catch((err) => {
                  console.error(err);
                  this.updateProcessing = false;
                  this.handleFlash({ response: err, show: true });
                });
            } else if (HOURS_FLOW_STATUSES.includes(selected)) {
              // reopen approved hours when the assignment is sent back into the hours flow
              this.reopenApprovedHours(item.id)
                .then((didReopen) => {
                  this.updateProcessing = false;
                  this.handleFlash({ response: resp, show: true });
                  // reload so the expansion re-fetches the now-pending hours and re-shows the controls
                  if (didReopen) this.$emit("assignment-dashboard-table-refresh");
                });
            } else {
              this.updateProcessing = false;
              this.handleFlash({ response: resp, show: true });
            }
          }
        })
        .catch((err) => {
          this.updateProcessing = false;
          this.handleFlash({ response: err, show: true });
          console.error(err);
        });
    },
    async reopenApprovedHours(assignmentId) {
      try {
        const resp = await getStaffHoursByAssignmentId(assignmentId);
        if (resp.status === 200 && Array.isArray(resp.data)) {
          const approved = resp.data.filter((el) => el.status === "APPROVED");
          if (!approved.length) return false;
          await Promise.all(
            approved.map((el) =>
              updateStaffHoursStatus({
                staffHoursId: el.id,
                status: { status: "PENDING" },
              })
            )
          );
          return true;
        }
      } catch (err) {
        console.error(err);
      }
      return false;
    },
    allowedToSelectStatuses() {
      let allowedStatuses = [...this[`${this.roleMode}Statuses`]];
      if (this.roleMode === "writer") {
        if (this.currentStatus === "Assigned") allowedStatuses.splice(1, 1);
        if (this.currentStatus === "Rejected") allowedStatuses.splice(0, 1);
      }
      return allowedStatuses;
    },
    allowedToUpdateStatuses() {
      const allowedStatuses = this[`${this.roleMode}Statuses`].filter(
        (el) => el != this[`${this.roleMode}Statuses`].slice(-1)
      );
      return this.roleMode !== "reviewer"
        ? allowedStatuses
        : this[`${this.roleMode}Statuses`];
    },
  },
};
</script>

<style lang="scss" scoped>
.status-selector {
  width: 120px;
  margin: 0 auto;

  &__activator {
    display: flex;
    justify-content: center;
  }

  &__chip {
    width: 100%;
    justify-content: space-between;
    font-family: "Roboto", sans-serif;
    font-weight: 500;
    font-size: 13px;
    line-height: 16px;
    cursor: pointer;
  }

  &__chip-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__chip-caret {
    margin-left: 2px;
  }
}

.status-selector__list {
  max-height: 420px;
  overflow-y: auto;
  padding-top: 4px;
  padding-bottom: 4px;
}

.status-selector__subheader {
  height: 28px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: #9aa0a6;
  text-transform: uppercase;
}

.status-selector__item-title {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #3c4043;
}
</style>
